import { useQuery } from '@tanstack/react-query'

import { isDevelopmentMode, type RuntimeMode } from '@/lib/data-source.ts'
import { queryKeys } from '@/lib/query/queryKeys.ts'
import { useTenant } from '@/tenant/useTenant.ts'

import { demoBusinessFor } from '../demo/demoBusiness.ts'
import type { Business } from '../model/business.ts'
import { fetchBusinessProfile } from './businessRepository.ts'

export type ProfileStatus = 'pending' | 'error' | 'success' | 'not-found'

export interface BusinessProfileResult {
  /** Null whenever the studio's own profile could not be read. */
  readonly business: Business | null
  readonly status: ProfileStatus
  readonly mode: RuntimeMode
  /** True only when the content on screen is illustrative. */
  readonly isDemo: boolean
  readonly error: Error | null
}

/**
 * The active studio's public profile.
 *
 * With no backend configured the local fixture is used and flagged. With a
 * backend there is **no fallback**: a failed read is an error and a missing
 * studio is a 404. Substituting fixtures for a real studio would put invented
 * prices and an invented address in front of a customer, which is worse than
 * showing nothing.
 */
export function useBusinessProfile(): BusinessProfileResult {
  const tenant = useTenant()

  const query = useQuery({
    queryKey: queryKeys.tenant(tenant.slug),
    queryFn: () => fetchBusinessProfile(tenant.slug),
    enabled: !isDevelopmentMode,
  })

  if (isDevelopmentMode) {
    return {
      business: demoBusinessFor(tenant.slug),
      status: 'success',
      mode: 'development',
      isDemo: true,
      error: null,
    }
  }

  if (query.status === 'pending') {
    return { business: null, status: 'pending', mode: 'live', isDemo: false, error: null }
  }

  if (query.status === 'error') {
    return { business: null, status: 'error', mode: 'live', isDemo: false, error: query.error }
  }

  if (query.data === null) {
    return { business: null, status: 'not-found', mode: 'live', isDemo: false, error: null }
  }

  return {
    business: query.data,
    status: 'success',
    // A studio reachable through `anon` is live: RLS only exposes live rows.
    mode: 'live',
    isDemo: false,
    error: null,
  }
}

/**
 * The profile when a screen genuinely cannot render without one.
 *
 * Returns the fixture only in development. In every other mode it returns
 * null and the caller must render an error or a skeleton — which is what
 * keeps a live studio from ever showing someone else's details.
 */
export function useBusinessProfileOrNull(): Business | null {
  return useBusinessProfile().business
}
